import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useSelectedDate } from "../lib/selectedDate";
import { useMe } from "../lib/useMe";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { Skeleton } from "../components/ui/Skeleton";
import { ErrorState } from "../components/ui/ErrorState";
import { SummaryCard } from "../components/SummaryCard";
import { DateStrip } from "../components/DateStrip";
import { EditSheet, type FieldSpec } from "../components/EditSheet";
import { ActivityTiles } from "../components/ActivityTiles";
import { FoodSheet } from "../components/FoodSheet";
import { WaterTracker } from "../components/WaterTracker";
import { WeightCard } from "../components/WeightCard";
import { CheckInSheet } from "../components/CheckInSheet";
import { AdjustmentBanner } from "../components/AdjustmentBanner";
import { SundayMaintenanceCard } from "../components/SundayMaintenanceCard";
import { RefeedBanner } from "../components/RefeedBanner";
import { RefeedSheet } from "../components/RefeedSheet";
import { groupEntries } from "../lib/meals";
import { mealTargets, macroTargets } from "../lib/diaryTargets";
import { todayLocal, addDays, unixToLocalDay } from "../lib/date";
import { MealGroup } from "../components/MealGroup";
import { SaveItemsSheet, type SaveCandidate } from "../components/SaveItemsSheet";
import { DayDetailsSheet } from "../components/DayDetailsSheet";
import { Paywall, type PaywallTrigger } from "../components/Paywall";
import { useEntitlement } from "../lib/useEntitlement";
import { FastingCard } from "../components/FastingCard";
import { WorkoutCard } from "../components/WorkoutCard";
import { WorkoutSheet } from "../components/WorkoutSheet";
import { ProteinSpreadSheet } from "../components/ProteinSpreadSheet";
import { MilestoneCard } from "../components/MilestoneCard";
import { hapticWarning } from "../lib/haptics";
import { saveFoodDraft, draftToEntry, draftFields, type FoodDraft } from "../lib/foodDraft";
import { onResume } from "../lib/appResume";
import { readSnapshot, writeSnapshot } from "../lib/snapshot";
import { syncReminders } from "../lib/reminderScheduler";
import { getReminderPrefs } from "../lib/reminderPrefs";
import {
  pendingId, withFoodAdded, withFoodUpdated, withFoodRemoved,
} from "../lib/optimistic";
import { Footprints } from "lucide-react";
import { SectionHeader } from "../components/ui/SectionHeader";
import type { EstimateResponse, TodayResponse, FoodEntry, Activity, MealSlot } from "@shared/types";

type FoodSheetState =
  | { open: false }
  | { open: true; mode: "add" | "edit"; initial: Partial<FoodEntry>; context: { eaten_on: string; source: "plan" | "custom" | "quick"; plan_meal_slot: MealSlot | null }; photo?: File; initialEstimate?: { response: EstimateResponse; transcript?: string } };

const MEAL_ICON: Record<MealSlot, string> = {
  breakfast: "🥣",
  lunch:     "🥗",
  dinner:    "🍽️",
  snack:     "🍫",
};

export function Today() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { selectedDate } = useSelectedDate();
  const { me } = useMe();
  // Week-in-review surfaces (card and teaser alike) only exist once there has
  // been a week to review.
  const accountWeekOld = !me?.created_at || selectedDate >= addDays(unixToLocalDay(me.created_at), 7);
  const confirm = useConfirm();
  const toast = useToast();
  // Render instantly from the last snapshot (stale-while-revalidate): the
  // cached copy paints on launch while the fresh response loads behind it.
  const [data, setData] = useState<TodayResponse | null>(() => readTodayCache(selectedDate));
  const [err, setErr] = useState<string | null>(null);
  const [foodSheet, setFoodSheet] = useState<FoodSheetState>({ open: false });
  const [editActivity, setEditActivity] = useState<Activity | null>(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showRefeed, setShowRefeed] = useState(false);
  // Two different reasons open this sheet — a spent AI allowance and a locked
  // check-in — and it names the one that brought you here.
  const [paywall, setPaywall] = useState<PaywallTrigger | null>(null);
  const [savingSlot, setSavingSlot] = useState<MealSlot | null>(null);
  const [showDayDetails, setShowDayDetails] = useState(false);

  /** Bookmark on a meal group: open the item picker — the user chooses
   *  which items to keep and whether they become one group or singles. */
  function saveSlotAsMeal(slot: MealSlot) {
    if (!data) return;
    if (data.entries.some((e) => e.plan_meal_slot === slot)) setSavingSlot(slot);
  }

  const saveCandidates: SaveCandidate[] = data && savingSlot
    ? data.entries.filter((e) => e.plan_meal_slot === savingSlot).map((e) => ({
        name: e.name, kcal: e.calories,
        protein_g: e.protein_g ?? null, carbs_g: e.carbs_g ?? null,
        fat_g: e.fat_g ?? null, sugar_g: e.sugar_g ?? null,
        micros_json: e.micros ? JSON.stringify(e.micros) : null,
        grams: null, unit: null,
      }))
    : [];
  // The check-in upsell may invite, never nag (owner decision): once closed
  // it stays closed across restarts. The Pro tab keeps the feature findable.
  const [teaserHidden, setTeaserHidden] = useState(() => {
    try { return localStorage.getItem("caloly.checkin_teaser_hidden") === "1"; }
    catch { return false; }
  });
  const dismissTeaser = () => {
    setTeaserHidden(true);
    try { localStorage.setItem("caloly.checkin_teaser_hidden", "1"); } catch { /* private mode */ }
  };
  const { billingEnabled } = useEntitlement();
  const [showWorkout, setShowWorkout] = useState(false);
  const [showProtein, setShowProtein] = useState(false);

  const ACTIVITY_FIELDS: FieldSpec[] = [
    { key: "duration_min", label: t("activity_picker.duration"),    type: "number" },
    { key: "distance_km",  label: t("activity_picker.distance"),    type: "number", step: 0.1 },
    { key: "kcal_burned",  label: t("activity_picker.kcal_burned"), type: "number" },
    { key: "note",         label: t("activity_picker.note"),        type: "text" },
  ];

  async function load() {
    try {
      const fresh = await api.today(selectedDate, apiLang(locale));
      setData(fresh);
      setErr(null);
    } catch (e) { setErr((e as Error).message); }
  }
  useEffect(() => { setData(readTodayCache(selectedDate)); load(); }, [selectedDate]);

  // One place writes the snapshot, so an optimistic day survives a relaunch the
  // same way a fetched one does.
  useEffect(() => { if (data) writeTodayCache(selectedDate, data); }, [data, selectedDate]);

  // Reminders are rebuilt from the day's state every time it changes. A local
  // notification scheduled this morning cannot know you logged lunch ten
  // minutes ago; replanning here is what makes "already done → say nothing"
  // true in practice. Only for the day that is actually today — scheduling from
  // a day being browsed in the past would be nonsense.
  useEffect(() => {
    if (!data || selectedDate !== todayLocal()) return;
    void getReminderPrefs().then((prefs) => {
      if (!prefs) return; // couldn't read settings — leave what's scheduled alone
      return syncReminders(data, prefs, {
        title: (kind) => t(`reminders.${kind}.title`),
        body: (kind) => t(`reminders.${kind}.body`),
      });
    });
  }, [data, selectedDate]);

  /**
   * Show the write, then make it. `apply` paints the change immediately, `write`
   * performs it, and `undo` takes it back if the server refuses — a rejected
   * write that leaves its row behind is a calorie number the user has no reason
   * to doubt. The rejection is rethrown so the caller can say what happened in
   * whatever place the user is still looking at; this helper never toasts,
   * because the sheet that submitted the write reports failures in place.
   *
   * Both transforms run through the state updater rather than against a snapshot
   * captured at call time: log two meals in quick succession and a snapshot-based
   * rollback would discard the first one along with the second. `undo` is the
   * inverse of `apply`, so it composes with whatever else has landed meanwhile.
   *
   * Note that `api` only throws on server rejections: a write that merely could
   * not reach the network is already queued for replay, so its row stays put.
   */
  async function optimistic(
    apply: (d: TodayResponse) => TodayResponse,
    undo: (d: TodayResponse) => TodayResponse,
    write: () => Promise<unknown>,
  ): Promise<void> {
    setData((cur) => (cur ? apply(cur) : cur));
    try {
      await write();
    } catch (e) {
      setData((cur) => (cur ? undo(cur) : cur));
      throw e;
    } finally {
      void load(); // the server is the authority, success or not
    }
  }

  /** FoodSheet hands drafts here — both new logs and edits. */
  async function persistFoodDraft(draft: FoodDraft): Promise<void> {
    if (draft.id === undefined) {
      const id = pendingId();
      await optimistic(
        (d) => withFoodAdded(d, draftToEntry(draft, id)),
        (d) => withFoodRemoved(d, id),
        () => saveFoodDraft(draft),
      );
      return;
    }
    // Editing: the inverse of the patch is the entry as it stands right now.
    const id = draft.id;
    const before = data?.entries.find((e) => e.id === id);
    await optimistic(
      (d) => withFoodUpdated(d, id, draftFields(draft)),
      (d) => (before ? withFoodUpdated(d, id, before) : d),
      () => saveFoodDraft(draft),
    );
  }

  async function removeEntry(id: number): Promise<void> {
    const before = data?.entries.find((e) => e.id === id);
    await optimistic(
      (d) => withFoodRemoved(d, id),
      (d) => (before ? withFoodAdded(d, before) : d),
      () => api.deleteFood(id),
    );
  }

  // Refresh when background syncs land (Apple Health steps, offline replays)
  // and whenever the app comes back — time passed, and the budget moves with
  // steps taken while caloly wasn't looking.
  useEffect(() => {
    const onSynced = () => { void load(); };
    window.addEventListener("caloly:health-synced", onSynced);
    window.addEventListener("caloly:offline-synced", onSynced);
    const offResume = onResume(onSynced);
    return () => {
      window.removeEventListener("caloly:health-synced", onSynced);
      window.removeEventListener("caloly:offline-synced", onSynced);
      offResume();
    };
  }, [selectedDate]);

  if (err && !data) return <ErrorState message={t("error.load_failed")} onRetry={load} />;
  if (!data) return <TodaySkeleton />;

  const eaten = data.consumed.calories;
  // Single budget rule lives server-side (functions/lib/today.ts).
  const budget = data.budget_kcal;
  const remaining = budget - eaten;
  const groups = groupEntries(data.entries);
  const mt = mealTargets(data.goals.daily_calories, data.goals);

  async function deleteEntry(e: FoodEntry) {
    if (await confirm({ message: t("confirm.delete_entry"), danger: true, confirmLabel: t("common.delete") })) {
      try { await removeEntry(e.id); hapticWarning(); toast.success(t("toast.deleted")); }
      catch { toast.error(t("toast.failed")); } // the row is already back
    }
  }

  function openAddForSlot(slot: MealSlot) {
    navigate(`/add?date=${selectedDate}&slot=${slot}`);
  }

  function openEditFood(e: FoodEntry) {
    setFoodSheet({
      open: true, mode: "edit", initial: e,
      context: { eaten_on: selectedDate, source: e.source, plan_meal_slot: e.plan_meal_slot },
    });
  }

  return (
    <div className="space-y-6 pt-4 animate-rise">
      {data.logged_days !== undefined && <MilestoneCard loggedDays={data.logged_days} />}
      {data.last_adjustment && <AdjustmentBanner adj={data.last_adjustment} />}
      {data.refeed_today && (
        <RefeedBanner
          weekNumber={data.refeed_today.week_number}
          targetKcal={data.refeed_today.target_kcal}
          milestone={data.refeed_today.milestone}
          onOpen={() => setShowRefeed(true)}
        />
      )}
      {data.tier === "free" && billingEnabled && !data.pending_check_in && !teaserHidden && accountWeekOld && (
        <div className="relative">
          <button
            onClick={() => setPaywall("check_in")}
            className="w-full bg-surface border border-line rounded-2xl p-4 pr-14 text-left flex items-center gap-3"
          >
            <Lock aria-hidden="true" size={22} className="text-fg-muted shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">{t("today.check_in_locked")}</div>
              <div className="text-sm text-fg-muted mt-0.5">{t("today.check_in_locked_hint")}</div>
            </div>
          </button>
          {/* Sibling, not a child — a button inside a button is invalid HTML.
              44px hit target per the touch rules. */}
          <button
            onPointerDown={(e) => { e.preventDefault(); dismissTeaser(); }}
            aria-label={t("common.dismiss")}
            className="absolute inset-y-0 right-0 w-12 flex items-center justify-center text-fg-muted press"
          >
            ✕
          </button>
        </div>
      )}
      {data.pending_check_in && (
        <button
          onClick={() => setShowCheckIn(true)}
          className="w-full bg-gradient-to-r from-success/20 to-accent-move/20 border border-success/30 rounded-2xl p-4 text-left flex items-center gap-3"
        >
          <span aria-hidden="true" className="text-2xl">📈</span>
          <div className="flex-1">
            <div className="text-sm font-medium">{t("today.check_in_card")}</div>
            <div className="text-sm text-fg-muted mt-0.5">
              {t("today.check_in_summary", {
                delta: data.pending_check_in.weight_delta_kg !== null
                  ? `${data.pending_check_in.weight_delta_kg > 0 ? "+" : ""}${data.pending_check_in.weight_delta_kg.toFixed(1)}`
                  : "—",
                adherence: data.pending_check_in.adherence_pct,
                kcal: (data.pending_check_in.suggestion?.delta_kcal ?? 0) > 0
                  ? `+${data.pending_check_in.suggestion?.delta_kcal}`
                  : String(data.pending_check_in.suggestion?.delta_kcal ?? 0),
              })}
            </div>
          </div>
          <span className="text-fg-faint">›</span>
        </button>
      )}
      <DateStrip />

      <SummaryCard
        eaten={eaten}
        burned={data.burned_kcal}
        remaining={remaining}
        budget={budget}
        consumed={data.consumed}
        targets={macroTargets(data.goals)}
        onDetails={() => setShowDayDetails(true)}
      />

      {data.sunday_maintenance && <SundayMaintenanceCard targetKcal={data.sunday_maintenance.target_kcal} />}

      {/* Meals-first (owner, 2026-08-09): the summary flows straight into the
          nutrition section — every logging method now lives on the add screen
          behind each meal's +, where the slot is already chosen. */}
      <section className="space-y-3">
        <SectionHeader
          action={
            <button onClick={() => setShowProtein(true)} className="text-sm text-fg-secondary underline press min-h-[44px]">
              {t("protein.spread_title")}
            </button>
          }
        >
          {t("today.nutrition")}
        </SectionHeader>
        <div className="space-y-3">
          {groups.map((group) => (
            <MealGroup
              key={group.slot}
              group={group}
              icon={MEAL_ICON[group.slot]}
              target={mt[group.slot]}
              onAdd={openAddForSlot}
              onSaveMeal={saveSlotAsMeal}
              onEdit={openEditFood}
              onDelete={deleteEntry}
            />
          ))}
        </div>
      </section>

      <WaterTracker
        consumedMl={data.consumed_water_ml}
        targetMl={data.goals.water_target_ml}
        drinks={data.drinks}
        selectedDate={selectedDate}
        onChange={load}
      />
      <FastingCard />

      <WorkoutCard
        date={selectedDate}
        sets={data.workouts}
        onOpen={() => setShowWorkout(true)}
        onChanged={load}
      />

      <section className="space-y-3">
        <SectionHeader>{t("weight.card_title")}</SectionHeader>
        <WeightCard
          latestWeightKg={data.latest_weight_kg}
          latestWeightOn={data.latest_weight_on}
          weighedToday={data.weighed_today}
          targetWeightKg={data.goals.target_weight_kg}
          selectedDate={selectedDate}
          onChange={load}
        />
      </section>

      <section className="space-y-3">
        <SectionHeader>{t("today.activities")}</SectionHeader>
        {data.steps != null && (
          <StepsCard steps={data.steps} target={data.goals.step_target} label={t("today.steps")} />
        )}
        <ActivityTiles
          activities={data.activities}
          onAdd={() => navigate(`/activity?date=${selectedDate}&w=${data.latest_weight_kg ?? (data.goals.start_weight_kg > 0 ? data.goals.start_weight_kg : 70)}`)}
          onEdit={(a) => {
            if (a.source === "healthkit") { toast.success(t("health.managed")); return; }
            setEditActivity(a);
          }}
        />
      </section>

      <FoodSheet
        open={foodSheet.open}
        mode={foodSheet.open ? foodSheet.mode : "add"}
        initial={foodSheet.open ? foodSheet.initial : {}}
        context={foodSheet.open ? foodSheet.context : { eaten_on: selectedDate, source: "custom", plan_meal_slot: null }}
        initialPhoto={foodSheet.open ? (foodSheet.photo ?? null) : null}
        initialEstimate={foodSheet.open ? (foodSheet.initialEstimate ?? null) : null}
        onSaved={persistFoodDraft}
        onDeleted={removeEntry}
        onClose={() => setFoodSheet({ open: false })}
      />

      <EditSheet<Activity>
        open={editActivity !== null}
        title={t("activity_picker.log_activity")}
        fields={ACTIVITY_FIELDS}
        initial={editActivity ?? {}}
        onSave={async (patch) => { if (editActivity) { await api.updateActivity(editActivity.id, patch); await load(); } }}
        onDelete={async () => { if (editActivity) { await api.deleteActivity(editActivity.id); await load(); } }}
        onClose={() => setEditActivity(null)}
      />

      {showCheckIn && data.pending_check_in && (
        <CheckInSheet
          checkIn={data.pending_check_in}
          target={data.goals.daily_calories}
          onApply={async () => {
            try {
              await api.actionCheckIn(data.pending_check_in!.id, "applied");
              await load();
              toast.success(t("toast.saved"));
            } catch { toast.error(t("toast.failed")); }
            setShowCheckIn(false);
          }}
          onDismiss={async () => {
            try {
              await api.actionCheckIn(data.pending_check_in!.id, "dismissed");
              await load();
              toast.success(t("toast.saved"));
            } catch { toast.error(t("toast.failed")); }
            setShowCheckIn(false);
          }}
          onClose={() => setShowCheckIn(false)}
        />
      )}

      {data.refeed_today && (
        <RefeedSheet
          open={showRefeed}
          weekNumber={data.refeed_today.week_number}
          targetKcal={data.refeed_today.target_kcal}
          onClose={() => setShowRefeed(false)}
        />
      )}

      {showDayDetails && data && <DayDetailsSheet day={data} onClose={() => setShowDayDetails(false)} />}
      {savingSlot && saveCandidates.length > 0 && (
        <SaveItemsSheet
          items={saveCandidates}
          defaultName={t(`meal.header.${savingSlot}`)}
          onClose={() => setSavingSlot(null)}
          onPaywall={() => setPaywall("feature")}
        />
      )}
      {paywall && <Paywall trigger={paywall} onClose={() => setPaywall(null)} />}
      {showProtein && (
        <ProteinSpreadSheet
          entries={data.entries}
          targetG={macroTargets(data.goals).protein_g}
          weightKg={data.latest_weight_kg}
          onClose={() => setShowProtein(false)}
        />
      )}
      {showWorkout && (
        <WorkoutSheet
          date={selectedDate}
          lastSessions={data.last_sessions}
          knownExercises={[...new Set(data.workouts.map((w) => w.exercise))]}
          onSaved={load}
          onClose={() => setShowWorkout(false)}
        />
      )}
    </div>
  );
}

// Snapshot for stale-while-revalidate rendering. Only the current day is kept:
// a past day's numbers never change, so re-fetching them costs nothing, and
// snapshotting every date browsed would grow without bound. Logging out clears
// the whole snapshot namespace (see lib/snapshot.ts).
//
// This does not use useCachedResource — Today owns its data for the optimistic
// mutations, which need setData transforms the hook has no business knowing
// about. It shares the store, which is what matters for correctness.
const TODAY_KEY = "today";

function readTodayCache(date: string): TodayResponse | null {
  const c = readSnapshot<{ date: string; data: TodayResponse }>(TODAY_KEY);
  return c && c.date === date ? c.data : null;
}

function writeTodayCache(date: string, data: TodayResponse): void {
  if (date === todayLocal()) writeSnapshot(TODAY_KEY, { date, data });
}

/** Slim daily-steps bar (fed by Apple Health sync or manual entry). */
function StepsCard({ steps, target, label }: { steps: number; target: number; label: string }) {
  const pct = target > 0 ? Math.min(100, Math.round((steps / target) * 100)) : 0;
  return (
    <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3">
      <span
        aria-hidden="true"
        className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
        style={{ background: "#06b6d4" }}
      >
        <Footprints size={18} strokeWidth={2.2} className="text-white" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">{label}</span>
          <span className="text-sm tabular-nums">
            <b>{steps.toLocaleString()}</b>
            {target > 0 && <span className="text-fg-muted"> / {target.toLocaleString()}</span>}
          </span>
        </div>
        <div className="mt-1.5 h-1.5 rounded-full bg-surface-strong overflow-hidden">
          <div className="h-full rounded-full bg-accent-move transition-[width] duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function TodaySkeleton() {
  return (
    <div className="space-y-6 pt-4">
      <Skeleton className="h-10 rounded-2xl" />
      <Skeleton className="h-64 rounded-3xl" />
      <Skeleton className="h-12 rounded-2xl" />
      <div className="space-y-3">
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
      </div>
    </div>
  );
}
